import React, { useEffect, useRef, useState } from 'react';
import { NavDropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { cn } from '../design-system';

export type NavMenuDropdownItem = {
  active?: boolean;
  className?: string;
  iconClassName?: string;
  key: string;
  label: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  to?: string;
};

type NavMenuDropdownProps = {
  active?: boolean;
  align?: 'start' | 'end';
  className?: string;
  hoverEnabled?: boolean;
  icon?: React.ReactNode;
  id: string;
  groupLabels?: Array<string | undefined>;
  itemGroups: NavMenuDropdownItem[][];
  label: string;
  onSelect?: () => void;
  theme?: string;
};

const NAV_DROPDOWN_HIDE_DELAY_MS = 80;

export default function NavMenuDropdown({
  active,
  align,
  className,
  hoverEnabled = true,
  icon,
  id,
  groupLabels,
  itemGroups,
  label,
  onSelect,
  theme = 'dark',
}: NavMenuDropdownProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const openDropdown = () => {
    clearHideTimer();
    setIsOpen(true);
  };

  const scheduleCloseDropdown = () => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      hideTimerRef.current = null;
    }, NAV_DROPDOWN_HIDE_DELAY_MS);
  };

  const closeDropdown = () => {
    clearHideTimer();
    setIsOpen(false);
  };

  const handlePointerEnter: React.PointerEventHandler<HTMLElement> = (event) => {
    if (event.pointerType !== 'touch') {
      openDropdown();
    }
  };

  const handlePointerLeave: React.PointerEventHandler<HTMLElement> = (event) => {
    if (event.pointerType !== 'touch') {
      scheduleCloseDropdown();
    }
  };

  useEffect(() => () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }
  }, []);

  return (
    <NavDropdown
      active={active}
      align={align}
      className={cn('nav-menu-dropdown bsui-scope', className)}
      data-bs-theme={theme}
      id={id}
      onPointerEnter={hoverEnabled ? handlePointerEnter : undefined}
      onPointerLeave={hoverEnabled ? handlePointerLeave : undefined}
      onSelect={() => {
        closeDropdown();
        onSelect?.();
      }}
      onToggle={(nextShow) => {
        clearHideTimer();
        setIsOpen(nextShow);
      }}
      renderMenuOnMount
      show={isOpen}
      title={(
        <>
          {icon}
          <span className="nav-menu-dropdown-label">{label}</span>
        </>
      )}
    >
      {itemGroups.map((group, groupIndex) => {
        const groupLabel = groupLabels?.[groupIndex];
        const groupLabelId = groupLabel ? `${id}-group-${groupIndex}-label` : undefined;

        return (
          <div
            aria-labelledby={groupLabelId}
            className="nav-menu-dropdown-group"
            key={`${id}-group-${groupIndex}`}
            role={groupLabel ? 'group' : undefined}
          >
            {groupLabel && (
              <span className="nav-menu-dropdown-group__label" id={groupLabelId}>
                {groupLabel}
              </span>
            )}
            {group.map((item) => {
              const content = (
                <>
                  {item.iconClassName ? (
                    <i className={`${item.iconClassName} nav-menu-dropdown-icon`} aria-hidden="true" />
                  ) : (
                    <span className="nav-menu-dropdown-icon" aria-hidden="true" />
                  )}
                  <span className="nav-menu-dropdown-item-label">{item.label}</span>
                </>
              );

              if (item.to) {
                return (
                  <NavDropdown.Item
                    active={item.active}
                    as={Link}
                    className={item.className}
                    key={item.key}
                    onClick={item.onClick}
                    to={item.to}
                  >
                    {content}
                  </NavDropdown.Item>
                );
              }

              return (
                <NavDropdown.Item
                  active={item.active}
                  className={item.className}
                  key={item.key}
                  onClick={item.onClick}
                >
                  {content}
                </NavDropdown.Item>
              );
            })}
            {groupIndex < itemGroups.length - 1 && <NavDropdown.Divider />}
          </div>
        );
      })}
    </NavDropdown>
  );
}
